import { Controller } from "@hotwired/stimulus"
import { animate } from "motion"

// Animation constants
const COUNTER_ANIMATION_DURATION = 0.8
const COUNTER_ANIMATION_DURATION_SHORT = 0.4
const HIDE_DELAY = 350

export default class extends Controller {
  static values = {
    missingCredits: Number,
    count: Number,
    initialised: Boolean,
    stillNeededSingularText: String,
    stillNeededPluralText: String,
  }

  static targets = ["missingCredits", "missingSection", "tag", "missingDecimal", "stillNeededText"]

  // ===== LIFECYCLE METHODS =====

  connect() {
    this._initializeState()
    this._setupInitialVisibility()
  }

  disconnect() {
    this._cleanup()
  }

  // ===== PUBLIC API =====

  show() {
    if (this.element.classList.contains("is-visible")) return

    this._clearTimers()
    this.element.classList.remove("hidden")

    this._showTimer = setTimeout(() => {
      this.element.classList.add("is-visible")
      this._showTimer = null
    }, 0)
  }

  hide() {
    if (this.element.classList.contains("hidden")) return

    this._clearTimers()
    this.element.classList.remove("is-visible")

    this._hideTimer = setTimeout(() => {
      this.element.classList.add("hidden")
      this._hideTimer = null
    }, HIDE_DELAY)
  }

  updateCount(value) {
    this.countValue = value
  }

  updateMissingCredits(value) {
    this.missingCreditsValue = value
  }

  // ===== VALUE CHANGE HANDLERS =====

  countValueChanged() {
    if (!this.initialisedValue) return
    // Count value is used for calculations but not displayed
  }

  missingCreditsValueChanged(newValue, oldValue) {
    if (!this.initialisedValue || !this.hasMissingCreditsTarget) return

    // Update visibility
    if (newValue === 0) {
      this.hide()
    } else {
      this.show()
    }

    this._handleMissingCreditsChange(newValue, oldValue)
  }

  // ===== PRIVATE METHODS =====

  _initializeState() {
    this._showTimer = null
    this._hideTimer = null
    this._missingAnimation = null
    this._missingLast = null
    this.initialisedValue = true
  }

  _setupInitialVisibility() {
    if (this.missingCreditsValue > 0) {
      this._forceShow()
    } else {
      this._forceHide()
    }
  }

  _cleanup() {
    this._clearTimers()
    this._cancelAnimations()
  }

  _handleMissingCreditsChange(newValue, oldValue) {
    this._cancelAnimation(this._missingAnimation)
    this._missingAnimation = null

    const from = this._getMissingCreditsFromValue(oldValue)
    const to = newValue

    this._animateMissingCreditsChange(from, to)
    this._updateMissingCreditsVisibility(to)
  }

  _animateMissingCreditsChange(from, to) {
    const isHalfValue = to % 1 === 0.5
    const delay = isHalfValue ? this._getAnimationDuration(from, to) * 1000 - 200 : 100

    // Update decimal visibility with delay
    setTimeout(() => {
      this._updateMissingDecimalVisibility(to)
    }, delay)

    // Update pluralization text with delay
    setTimeout(() => {
      this._updateStillNeededText(to)
    }, 200)

    this.toggleMissingCredits(to > 0)

    if (to === 0) {
      this._handleZeroMissingCredits()
      return
    }

    this._missingAnimation = animate(from, to, {
      duration: this._getAnimationDuration(from, to),
      ease: "circOut",
      onUpdate: (latest) => {
        this._missingLast = latest
        this.missingCreditsTarget.textContent = Math.floor(latest)
      },
    })

    this._missingAnimation.finished?.then?.(() => {
      if (this.missingCreditsValue === to) {
        this._missingLast = null
        this._updateMissingDecimalVisibility(to)
      }
    })
  }

  _handleZeroMissingCredits() {
    this.missingCreditsTarget.textContent = "0"
    this._updateMissingDecimalVisibility(0)
    this._missingLast = 0
  }

  _getMissingCreditsFromValue(oldValue) {
    const domNow = parseFloat(this.missingCreditsTarget.textContent)
    return this._missingLast || domNow || oldValue || 0
  }

  _updateMissingCreditsVisibility(to) {
    this.toggleMissingCredits(to > 0)
  }

  // ===== VISIBILITY METHODS =====

  _forceShow() {
    this._clearTimers()
    this.element.classList.remove("hidden")
    this.element.classList.add("is-visible")
  }

  _forceHide() {
    this._clearTimers()
    this.element.classList.remove("is-visible")
    this.element.classList.add("hidden")
  }

  toggleMissingCredits(show) {
    if (this.hasMissingSectionTarget) {
      this.missingSectionTarget.classList.toggle("is-visible", !!show)
    }
    // Tag type is always default - no conditional styling
  }

  // ===== DECIMAL VISIBILITY METHODS =====

  _updateMissingDecimalVisibility(value) {
    if (this.hasMissingDecimalTarget) {
      const isHalfValue = value % 1 === 0.5
      this.missingDecimalTarget.classList.toggle("is-visible", isHalfValue)
    }
  }

  _updateStillNeededText(value) {
    if (this.hasStillNeededTextTarget) {
      const text = value === 1 ? this.stillNeededSingularTextValue : this.stillNeededPluralTextValue
      this.stillNeededTextTarget.textContent = text
    }
  }

  // ===== UTILITY METHODS =====

  _getAnimationDuration(from, to) {
    return Math.abs(to - from) <= 7 ? COUNTER_ANIMATION_DURATION_SHORT : COUNTER_ANIMATION_DURATION
  }

  _clearTimers() {
    if (this._showTimer) {
      clearTimeout(this._showTimer)
      this._showTimer = null
    }
    if (this._hideTimer) {
      clearTimeout(this._hideTimer)
      this._hideTimer = null
    }
  }

  _cancelAnimations() {
    this._cancelAnimation(this._missingAnimation)
    this._missingAnimation = null
  }

  _cancelAnimation(animation) {
    try {
      animation?.cancel?.()
    } catch {
      // ignore errors
    }
    try {
      animation?.stop?.()
    } catch {
      // ignore errors
    }
  }
}
